import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi } from 'lucide-react';

/** Listens for online/offline events + registers the CODEX service worker.
    Shows a non-intrusive bottom banner when offline. */
export default function OfflineIndicator() {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [justReconnected, setJustReconnected] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/codex-sw.js', { scope: '/codex/' }).catch(() => {});
    }
    const onlineHandler = () => {
      setOnline(true);
      setJustReconnected(true);
      setTimeout(() => setJustReconnected(false), 2500);
    };
    const offlineHandler = () => setOnline(false);
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }, []);

  return (
    <AnimatePresence>
      {!online && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          data-testid="codex-offline-banner"
          style={{
            position: 'fixed', bottom: 12, left: 12, right: 12, zIndex: 100,
            display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          }}
        >
          <div
            className="codex-card"
            style={{
              padding: '10px 18px',
              display: 'inline-flex', alignItems: 'center', gap: 10,
              borderColor: 'rgba(125,211,252,0.5)',
              background: 'rgba(11,10,22,0.92)',
              fontSize: 13,
              pointerEvents: 'auto',
            }}
          >
            <WifiOff size={16} color="#7dd3fc" />
            <span style={{ opacity: 0.9 }}>Offline — viewing cached scrolls. Progress will sync when you reconnect.</span>
          </div>
        </motion.div>
      )}
      {justReconnected && online && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          style={{
            position: 'fixed', bottom: 12, left: 12, right: 12, zIndex: 100,
            display: 'flex', justifyContent: 'center', pointerEvents: 'none',
          }}
        >
          <div
            className="codex-card"
            style={{
              padding: '10px 18px',
              display: 'inline-flex', alignItems: 'center', gap: 10,
              borderColor: 'rgba(34,197,94,0.5)',
              background: 'rgba(11,10,22,0.92)',
              fontSize: 13,
            }}
          >
            <Wifi size={16} color="#22c55e" />
            <span>Reconnected. Welcome back.</span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
